import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, ChevronUp, ChevronDown, Maximize2, Minimize2 } from 'lucide-react';

/**
 * Modal custom avec footer fixe, déplacement ET redimensionnement manuel
 */
export const CustomModal = ({ isOpen, onClose, title, children, footer, maxWidth = '500px', maxHeight = '85vh' }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  
  // États pour le redimensionnement
  const [size, setSize] = useState({ width: maxWidth, height: maxHeight });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [isMaximized, setIsMaximized] = useState(false);
  const [preMaximizeState, setPreMaximizeState] = useState(null);
  
  const bodyRef = useRef(null);

  const handleMouseDown = (e) => {
    // Ne commencer le drag que si on clique sur le header (pas sur le bouton X ou maximize)
    if (e.target.closest('button')) return;
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      setPosition({ x: newX, y: newY });
    }
    
    if (isResizing && resizeDirection) {
      handleResize(e);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeDirection(null);
  };

  // Fonctions de redimensionnement
  const handleResizeStart = (direction, e) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
    
    // Convertir la taille actuelle en pixels pour le redimensionnement
    const modalElement = e.target.closest('[data-modal-content]');
    const currentWidth = modalElement?.offsetWidth || parseFloat(size.width);
    const currentHeight = modalElement?.offsetHeight || parseFloat(size.height);
    
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: currentWidth,
      height: currentHeight
    });
  };

  const handleResize = (e) => {
    if (!resizeDirection) return;

    const deltaX = e.clientX - resizeStart.x;
    const deltaY = e.clientY - resizeStart.y;

    let newWidth = resizeStart.width;
    let newHeight = resizeStart.height;
    let newX = position.x;
    let newY = position.y;

    // Largeur minimale et maximale
    const minWidth = 300;
    const maxWidthPx = window.innerWidth * 0.95;
    const minHeight = 200;
    const maxHeightPx = window.innerHeight * 0.95;

    switch (resizeDirection) {
      case 'e': // Est (droite)
        newWidth = Math.min(Math.max(resizeStart.width + deltaX, minWidth), maxWidthPx);
        break;
      case 'w': // Ouest (gauche)
        newWidth = Math.min(Math.max(resizeStart.width - deltaX, minWidth), maxWidthPx);
        newX = position.x + (resizeStart.width - newWidth);
        break;
      case 's': // Sud (bas)
        newHeight = Math.min(Math.max(resizeStart.height + deltaY, minHeight), maxHeightPx);
        break;
      case 'n': // Nord (haut)
        newHeight = Math.min(Math.max(resizeStart.height - deltaY, minHeight), maxHeightPx);
        newY = position.y + (resizeStart.height - newHeight);
        break;
      case 'se': // Sud-Est (coin bas-droite)
        newWidth = Math.min(Math.max(resizeStart.width + deltaX, minWidth), maxWidthPx);
        newHeight = Math.min(Math.max(resizeStart.height + deltaY, minHeight), maxHeightPx);
        break;
      case 'sw': // Sud-Ouest (coin bas-gauche)
        newWidth = Math.min(Math.max(resizeStart.width - deltaX, minWidth), maxWidthPx);
        newHeight = Math.min(Math.max(resizeStart.height + deltaY, minHeight), maxHeightPx);
        newX = position.x + (resizeStart.width - newWidth);
        break;
      case 'ne': // Nord-Est (coin haut-droite)
        newWidth = Math.min(Math.max(resizeStart.width + deltaX, minWidth), maxWidthPx);
        newHeight = Math.min(Math.max(resizeStart.height - deltaY, minHeight), maxHeightPx);
        newY = position.y + (resizeStart.height - newHeight);
        break;
      case 'nw': // Nord-Ouest (coin haut-gauche)
        newWidth = Math.min(Math.max(resizeStart.width - deltaX, minWidth), maxWidthPx);
        newHeight = Math.min(Math.max(resizeStart.height - deltaY, minHeight), maxHeightPx);
        newX = position.x + (resizeStart.width - newWidth);
        newY = position.y + (resizeStart.height - newHeight);
        break;
    }

    setSize({ width: `${newWidth}px`, height: `${newHeight}px` });
    setPosition({ x: newX, y: newY });
  };

  // Fonction pour maximiser/restaurer le modal
  const toggleMaximize = () => {
    if (isMaximized) {
      // Restaurer
      if (preMaximizeState) {
        setSize(preMaximizeState.size);
        setPosition(preMaximizeState.position);
      }
      setIsMaximized(false);
    } else {
      // Maximiser
      setPreMaximizeState({ size, position });
      setSize({ width: '95vw', height: '95vh' });
      setPosition({ x: 0, y: 0 });
      setIsMaximized(true);
    }
  };

  // Vérifier si le contenu est scrollable
  const checkScroll = () => {
    if (bodyRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = bodyRef.current;
      setShowScrollTop(scrollTop > 20);
      setShowScrollBottom(scrollTop + clientHeight < scrollHeight - 20);
    }
  };

  // Scroll vers le haut
  const scrollToTop = () => {
    if (bodyRef.current) {
      bodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Scroll vers le bas
  const scrollToBottom = () => {
    if (bodyRef.current) {
      bodyRef.current.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' });
    }
  };

  // Ajouter les event listeners pour le drag et resize
  React.useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart, position, resizeDirection, resizeStart]);

  // Reset position and size when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setPosition({ x: 0, y: 0 });
      setSize({ width: maxWidth, height: maxHeight });
      setIsMaximized(false);
      // Ajouter classe au body pour gérer le z-index global
      document.body.classList.add('modal-active');
      // Vérifier le scroll après un petit délai pour laisser le contenu se rendre
      setTimeout(checkScroll, 100);
    } else {
      // Retirer la classe quand le modal se ferme
      document.body.classList.remove('modal-active');
    }
    
    // Cleanup au démontage
    return () => {
      document.body.classList.remove('modal-active');
    };
  }, [isOpen]);

  // Vérifier le scroll quand le contenu change
  useEffect(() => {
    if (isOpen && bodyRef.current) {
      checkScroll();
      const observer = new MutationObserver(checkScroll);
      observer.observe(bodyRef.current, { childList: true, subtree: true });
      return () => observer.disconnect();
    }
  }, [isOpen, children]);

  if (!isOpen) return null;

  // Ne pas utiliser de portal, juste rendre dans le flux normal avec position fixed
  const modalContent = (
    <>
      {/* Overlay - FOND NOIR 40% */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.70)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          animation: 'fadeIn 0.2s ease-out',
          pointerEvents: 'auto',
          zIndex: 10000
        }}
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div
        data-modal-content
        style={{
          position: 'fixed',
          left: '50%',
          top: '50%',
          transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 30px 60px rgba(0, 0, 0, 1)',
          width: typeof size.width === 'string' ? size.width : `${size.width}px`,
          height: typeof size.height === 'string' ? size.height : `${size.height}px`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          cursor: isDragging ? 'grabbing' : 'default',
          animation: isMaximized ? 'none' : 'modalZoomIn 0.2s ease-out',
          pointerEvents: 'auto',
          transition: isMaximized ? 'all 0.3s ease' : 'none',
          zIndex: 10001
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handles de redimensionnement - 8 directions */}
        {!isMaximized && (
          <>
            {/* Handles sur les côtés */}
            <div
              onMouseDown={(e) => handleResizeStart('n', e)}
              style={{
                position: 'absolute',
                top: 0,
                left: '10px',
                right: '10px',
                height: '6px',
                cursor: 'ns-resize',
                zIndex: 10
              }}
              title="Redimensionner (haut)"
            />
            <div
              onMouseDown={(e) => handleResizeStart('s', e)}
              style={{
                position: 'absolute',
                bottom: 0,
                left: '10px',
                right: '10px',
                height: '6px',
                cursor: 'ns-resize',
                zIndex: 10
              }}
              title="Redimensionner (bas)"
            />
            <div
              onMouseDown={(e) => handleResizeStart('e', e)}
              style={{
                position: 'absolute',
                right: 0,
                top: '10px',
                bottom: '10px',
                width: '6px',
                cursor: 'ew-resize',
                zIndex: 10
              }}
              title="Redimensionner (droite)"
            />
            <div
              onMouseDown={(e) => handleResizeStart('w', e)}
              style={{
                position: 'absolute',
                left: 0,
                top: '10px',
                bottom: '10px',
                width: '6px',
                cursor: 'ew-resize',
                zIndex: 10
              }}
              title="Redimensionner (gauche)"
            />
            
            {/* Handles sur les coins */}
            <div
              onMouseDown={(e) => handleResizeStart('nw', e)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '12px',
                height: '12px',
                cursor: 'nwse-resize',
                zIndex: 11
              }}
              title="Redimensionner (coin haut-gauche)"
            />
            <div
              onMouseDown={(e) => handleResizeStart('ne', e)}
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '12px',
                height: '12px',
                cursor: 'nesw-resize',
                zIndex: 11
              }}
              title="Redimensionner (coin haut-droit)"
            />
            <div
              onMouseDown={(e) => handleResizeStart('sw', e)}
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '12px',
                height: '12px',
                cursor: 'nesw-resize',
                zIndex: 11
              }}
              title="Redimensionner (coin bas-gauche)"
            />
            <div
              onMouseDown={(e) => handleResizeStart('se', e)}
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: '12px',
                height: '12px',
                cursor: 'nwse-resize',
                zIndex: 11,
                background: 'linear-gradient(135deg, transparent 50%, #9ca3af 50%)',
                borderBottomRightRadius: '8px'
              }}
              title="Redimensionner (coin bas-droit)"
            />
          </>
        )}
        
        {/* Header - Zone de drag */}
        <div 
          onMouseDown={handleMouseDown}
          style={{ 
            padding: '16px 20px', 
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
            backgroundColor: '#f9fafb',
            position: 'relative'
          }}
        >
          <h2 style={{ 
            margin: 0, 
            fontSize: '18px', 
            fontWeight: 600,
            color: '#111827',
            flex: 1
          }}>
            {title}
          </h2>
          
          {/* Info bulle pour les utilisateurs */}
          {!isMaximized && (
            <div style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '11px',
              color: '#9ca3af',
              fontWeight: 400,
              pointerEvents: 'none',
              userSelect: 'none'
            }}>
              🖱️ Déplacer | 📐 Redimensionner sur les bords
            </div>
          )}
          
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={toggleMaximize}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              title={isMaximized ? "Restaurer" : "Maximiser"}
              aria-label={isMaximized ? "Restaurer" : "Maximiser"}
            >
              {isMaximized ? <Minimize2 size={18} color="#6b7280" /> : <Maximize2 size={18} color="#6b7280" />}
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#fee2e2'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              aria-label="Fermer"
            >
              <X size={20} color="#dc2626" />
            </button>
          </div>
        </div>
        
        {/* Body - Scrollable avec indicateurs */}
        <div 
          ref={bodyRef}
          onScroll={checkScroll}
          style={{
            padding: '20px',
            overflowY: 'auto',
            overflowX: 'hidden',
            flexGrow: 1,
            minHeight: 0,
            position: 'relative',
            scrollBehavior: 'smooth'
          }}
        >
          {/* Indicateur de scroll haut (ombre) */}
          {showScrollTop && (
            <div style={{
              position: 'sticky',
              top: '-20px',
              left: 0,
              right: 0,
              height: '20px',
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.1), transparent)',
              pointerEvents: 'none',
              zIndex: 1,
              marginBottom: '-20px'
            }} />
          )}
          
          {children}
          
          {/* Indicateur de scroll bas (ombre) */}
          {showScrollBottom && (
            <div style={{
              position: 'sticky',
              bottom: '-20px',
              left: 0,
              right: 0,
              height: '20px',
              background: 'linear-gradient(to top, rgba(0,0,0,0.1), transparent)',
              pointerEvents: 'none',
              zIndex: 1,
              marginTop: '-20px'
            }} />
          )}
        </div>
        
        {/* Boutons de navigation scroll - Flottants */}
        {(showScrollTop || showScrollBottom) && (
          <div style={{
            position: 'absolute',
            right: '20px',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}>
            {showScrollTop && (
              <button
                onClick={scrollToTop}
                style={{
                  background: 'white',
                  border: '2px solid #e5e7eb',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f3f4f6';
                  e.target.style.borderColor = '#9ca3af';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.borderColor = '#e5e7eb';
                }}
                title="Scroller vers le haut"
              >
                <ChevronUp size={20} color="#374151" />
              </button>
            )}
            
            {showScrollBottom && (
              <button
                onClick={scrollToBottom}
                style={{
                  background: 'white',
                  border: '2px solid #e5e7eb',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f3f4f6';
                  e.target.style.borderColor = '#9ca3af';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.borderColor = '#e5e7eb';
                }}
                title="Scroller vers le bas"
              >
                <ChevronDown size={20} color="#374151" />
              </button>
            )}
          </div>
        )}

        {/* Footer - Fixed si fourni */}
        {footer && (
          <div style={{
            padding: '16px 20px',
            borderTop: '1px solid #e5e7eb',
            flexShrink: 0,
            backgroundColor: 'white',
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px'
          }}>
            {footer}
          </div>
        )}
      </div>
      
      {/* Animations CSS pour le modal */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes modalZoomIn {
          from {
            opacity: 0;
            transform: translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px)) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px)) scale(1);
          }
        }
      `}</style>
    </>
  );
  
  // Rendre directement sans portal
  return modalContent;
};
